import { Coordinates } from '../types';
import i18n from '../i18n';

// Konfigurerbar rutemotor: sett VITE_OSRM_URL for å peke på egen/betalt OSRM-instans
// (fjerner demo-serverens rate-limit for premium/lange turer). Faller tilbake til
// den offentlige demo-serveren når env ikke er satt.
const OSRM_BASE_URL = (import.meta as any).env?.VITE_OSRM_URL || 'https://router.project-osrm.org/route/v1/driving';
// FOSSGIS-hostet OSRM støtter fot-profil (den offentlige demo-serveren gjør ikke det).
const OSRM_FOOT_URL = 'https://routing.openstreetmap.de/routed-foot/route/v1/foot';
const CONTENT_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';

export interface DrivingRouteResult {
  coordinates: [number, number][]; // [lon, lat] for GeoJSON
  distanceMeters: number;
  durationSeconds: number;
  source: 'osrm';
}

const fetchJsonWithProxyFallback = async (url: string): Promise<any> => {
  const tryDirect = async () => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`direct_${res.status}`);
    return res.json();
  };

  const tryProxy = async () => {
    const proxiedUrl = `${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxiedUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`proxy_${res.status}`);
    return res.json();
  };

  try {
    return await tryDirect();
  } catch {
    return await tryProxy();
  }
};

// Enkel in-memory cache så vi ikke spør OSRM/FOSSGIS på nytt for samme strekning.
const routeCache = new Map<string, DrivingRouteResult>();
const ROUTE_CACHE_MAX = 200;
const coordKey = (c: Coordinates) => `${c.latitude.toFixed(4)},${c.longitude.toFixed(4)}`;

const fetchOsrmRoute = async (baseUrl: string, profile: string, from: Coordinates, to: Coordinates): Promise<DrivingRouteResult> => {
  if (!from || !to) throw new Error('missing_coordinates');
  const cacheKey = `${profile}:${coordKey(from)};${coordKey(to)}`;
  const cached = routeCache.get(cacheKey);
  if (cached) return cached;

  const url = `${baseUrl}/${from.longitude},${from.latitude};${to.longitude},${to.latitude}?overview=full&geometries=geojson&steps=false&alternatives=false`;
  const payload = await fetchJsonWithProxyFallback(url);

  if (payload?.code !== 'Ok') {
    throw new Error(`${profile}_${payload?.code || 'unknown'}`);
  }

  const route = Array.isArray(payload?.routes) ? payload.routes[0] : null;
  const coords = Array.isArray(route?.geometry?.coordinates) ? route.geometry.coordinates : [];
  if (!route || coords.length < 2) {
    throw new Error(`${profile}_no_geometry`);
  }

  const result: DrivingRouteResult = {
    coordinates: coords,
    distanceMeters: Number(route.distance) || 0,
    durationSeconds: Number(route.duration) || 0,
    source: 'osrm'
  };
  if (routeCache.size >= ROUTE_CACHE_MAX) {
    const oldest = routeCache.keys().next().value;
    if (oldest !== undefined) routeCache.delete(oldest);
  }
  routeCache.set(cacheKey, result);
  return result;
};

export const fetchDrivingRoute = (from: Coordinates, to: Coordinates): Promise<DrivingRouteResult> =>
  fetchOsrmRoute(OSRM_BASE_URL, 'osrm', from, to);

export const fetchWalkingRoute = (from: Coordinates, to: Coordinates): Promise<DrivingRouteResult> =>
  fetchOsrmRoute(OSRM_FOOT_URL, 'osrm_foot', from, to);

export const fetchDrivingRouteAlternatives = async (
  from: Coordinates,
  to: Coordinates,
  maxAlternatives: number = 3
): Promise<DrivingRouteResult[]> => {
  if (!from || !to) throw new Error('missing_coordinates');
  const url = `${OSRM_BASE_URL}/${from.longitude},${from.latitude};${to.longitude},${to.latitude}?overview=full&geometries=geojson&steps=false&alternatives=true`;
  const payload = await fetchJsonWithProxyFallback(url);

  if (payload?.code !== 'Ok') {
    throw new Error(`osrm_${payload?.code || 'unknown'}`);
  }

  const routes = Array.isArray(payload?.routes) ? payload.routes : [];
  return routes
    .slice(0, maxAlternatives)
    .filter((r: any) => Array.isArray(r?.geometry?.coordinates) && r.geometry.coordinates.length >= 2)
    .map((route: any) => ({
      coordinates: route.geometry.coordinates as [number, number][],
      distanceMeters: Number(route.distance) || 0,
      durationSeconds: Number(route.duration) || 0,
      source: 'osrm' as const,
    }));
};

/** Route through multiple waypoints (3+ coordinates). Returns a single route. */
export const fetchMultiWaypointRoute = async (
  waypoints: Coordinates[]
): Promise<DrivingRouteResult> => {
  if (!waypoints || waypoints.length < 2) throw new Error('missing_coordinates');
  const coordStr = waypoints.map(w => `${w.longitude},${w.latitude}`).join(';');
  const url = `${OSRM_BASE_URL}/${coordStr}?overview=full&geometries=geojson&steps=false&alternatives=false`;
  const payload = await fetchJsonWithProxyFallback(url);

  if (payload?.code !== 'Ok') {
    throw new Error(`osrm_${payload?.code || 'unknown'}`);
  }

  const route = Array.isArray(payload?.routes) ? payload.routes[0] : null;
  const coords = Array.isArray(route?.geometry?.coordinates) ? route.geometry.coordinates : [];
  if (!route || coords.length < 2) {
    throw new Error('osrm_no_geometry');
  }

  return {
    coordinates: coords,
    distanceMeters: Number(route.distance) || 0,
    durationSeconds: Number(route.duration) || 0,
    source: 'osrm',
  };
};

export const formatDuration = (seconds: number): string => {
  const isEn = (i18n.resolvedLanguage || i18n.language || "no").toLowerCase().startsWith("en");
  const totalMin = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;

  if (isEn) {
    if (h <= 0) return `${m} min`;
    return `${h} h ${m.toString().padStart(2, '0')} min`;
  }

  if (h <= 0) return `${m} min`;
  return `${h} t ${m.toString().padStart(2, '0')} min`;
};
